/**
 * The message request prompt (PPL-429) — built from Carl's canvas
 * (`pplkpr-message-request.html`, artboards mr-web-modal / mr-web-loading /
 * mr-web-error).
 *
 * Six zones in one order: eyebrow, identity, high-level details, the message,
 * actions, footnote. Two rules run through all of it:
 *
 *  - **The handle is the headline.** DMs are the anonymous half, so a first
 *    name never appears here, in any state. The payload the server sends
 *    cannot carry one.
 *  - **The message is in-app only.** It is shown here and never in the email,
 *    the push, or the inbox row.
 *
 * Dismissing (✕, the scrim, Escape) decides nothing and does not re-prompt:
 * the request keeps waiting in the inbox, which is its durable home.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreHorizontal, X } from 'react-feather';
import { blockUser, respondToMessageRequest } from '../services/messageService';
import { forgetRequest, hasBeenPrompted, markPrompted, useMessageRequests } from '../hooks/useMessageRequests';
import { trackRequestAnswered, trackRequestPromptShown } from './messageRequestAnalytics';
import type { RequestSource } from './messageRequestAnalytics';
import type { MessageRequest } from '../types/social';
import { normalizeInterests } from '../utils/interests';
import '../styles/messageRequest.css';

/**
 * The canvas's handle avatar: their photo when they have one, otherwise a
 * gradient generated from the handle with its first letter. Never initials of
 * a name — there is no name here to take initials from.
 */
function RequestAvatar({ handle, src, size = 48 }: { handle: string; src?: string | null; size?: number }) {
  const bare = handle.replace(/^@+/, '');
  let hue = 7;
  for (const ch of bare) hue = (hue * 31 + ch.charCodeAt(0)) % 360;
  if (src) {
    return <img className="mr-avatar" src={src} alt="" width={size} height={size} style={{ width: size, height: size }} />;
  }
  return (
    <div
      className="mr-avatar"
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.36),
        background: `linear-gradient(140deg, oklch(0.64 0.16 ${hue}), oklch(0.48 0.19 ${(hue + 62) % 360}))`,
      }}
    >
      {bare.charAt(0).toUpperCase()}
    </div>
  );
}

/** "active today" / "active this week" / "active this month" — the canvas's chip. */
function activityLabel(lastActive: string | null): string | null {
  if (!lastActive) return null;
  const ms = Date.now() - new Date(lastActive).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const days = ms / 86400000;
  if (days < 1) return 'active today';
  if (days < 7) return 'active this week';
  if (days < 31) return 'active this month';
  return null;
}

/** "2 hours ago" / "3d" — the canvas's caption, from the ISO the server sends. */
function sinceLabel(iso: string | null): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

type Busy = null | 'accept' | 'decline' | 'block';

export function MessageRequestCard({
  request,
  more,
  source,
  onClose,
  onAnswered,
}: {
  request: MessageRequest;
  more: number;
  /** Which host is showing this card — the analytics `source` (PPL-432). */
  source: Extract<RequestSource, 'prompt' | 'inbox'>;
  onClose: () => void;
  onAnswered: (chatId: string, action: 'accept' | 'decline' | 'block') => void;
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState('');
  const [confirmBlock, setConfirmBlock] = useState(false);

  // Escape inside the block confirmation backs out of the confirmation. The
  // host's Escape would otherwise dismiss the whole request and mark it
  // prompted, so it would never open again.
  useEffect(() => {
    if (!confirmBlock) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setConfirmBlock(false);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [confirmBlock]);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const acceptRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const { requester, firstMessage } = request;
  const handle = requester.tetherHandle?.startsWith('@')
    ? requester.tetherHandle
    : `@${requester.tetherHandle || 'user'}`;
  // A sender whose account is gone: no profile to show and nothing to accept.
  const unavailable = !requester.id;
  const interests = normalizeInterests(requester.interests).slice(0, 3);
  // The server orders shared interests first, so the first N chips are the
  // common ground — that is what turns "into" into "you both like".
  const sharedCount = (requester.sharedInterests || []).length;
  const activity = activityLabel(requester.lastActive);
  const hasDetails = interests.length > 0 || !!requester.lookingFor || !!requester.aboutMe;

  useEffect(() => {
    // PPL-432: one dm_request_prompt_shown per request, not per render or per
    // mount. Both hosts of this card go through here; the de-duplication is
    // the analytics module's (StrictMode invokes this effect twice in dev).
    trackRequestPromptShown(request.chatId);
  }, [request.chatId]);

  useEffect(() => {
    // Whatever had focus before the prompt opened gets it back when the
    // prompt closes — once, on the way out, not on every view change inside.
    const previous = document.activeElement as HTMLElement | null;
    return () => previous?.focus?.();
  }, [request.chatId]);

  useEffect(() => {
    // The card itself is the fallback: an unavailable sender has no accept
    // button, and focus would otherwise never enter a dialog that claims
    // aria-modal. Keyed on the block confirmation too, because that swaps in
    // a different dialog subtree and would otherwise leave focus on the body.
    (acceptRef.current ?? cardRef.current)?.focus();
  }, [request.chatId, confirmBlock]);

  // Tab stays inside the dialog while it is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !cardRef.current) return;
      const focusable = cardRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && (active === first || active === cardRef.current)) {
        e.preventDefault();
        last.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [request.chatId]);

  const answer = useCallback(
    async (action: 'accept' | 'decline') => {
      if (busy) return;
      setBusy(action);
      setError('');
      try {
        // The server's answer is the truth: this request may already have been
        // answered on another device inside the poll window.
        const {status, changed} = await respondToMessageRequest(request.chatId, action);
        const answered = status === 'accepted' ? 'accept' : status === 'declined' ? 'decline' : action;
        // `changed` false means it was already answered — on another device,
        // inside the poll window. That answer has been counted already.
        if (changed) trackRequestAnswered(answered, source);
        forgetRequest(request.chatId);
        // Both directions: a request answered on another device inside the
        // poll window may come back either way, and the toast and the
        // navigation must follow the server, not the button that was pressed.
        onAnswered(request.chatId, answered);
      } catch {
        // Neutral, and true: nothing was lost.
        setError("couldn't do that — it's still waiting in requests");
        setBusy(null);
      }
    },
    [busy, request.chatId, onAnswered, source]
  );

  async function doBlock() {
    if (busy) return;
    setBusy('block');
    setError('');
    try {
      await blockUser(requester.id);
      trackRequestAnswered('block', source);
      // Blocking is the stronger action, and it also answers the request. It
      // is one dm_request_blocked, never also a dm_request_declined.
      await respondToMessageRequest(request.chatId, 'decline').catch(() => {});
      forgetRequest(request.chatId);
      onAnswered(request.chatId, 'block');
    } catch {
      setError("couldn't do that — it's still waiting in requests");
      setBusy(null);
    }
  }

  function viewProfile() {
    // Leaves the request pending on purpose: looking is not answering.
    markPrompted(request.chatId);
    onClose();
    navigate(`/user/${requester.id}`, { state: { from: 'message_request', chatId: request.chatId } });
  }

  if (confirmBlock) {
    return (
      <div className="mr-card mr-card--confirm" ref={cardRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={`block ${handle}`}>
        <div className="mr-confirm-head">
          <RequestAvatar handle={handle} src={requester.profileImageUrl} size={40} />
          <h2 className="mr-confirm-title">block {handle}?</h2>
        </div>
        <p className="mr-confirm-body">
          they won't be able to message or invite you, and any room with them closes.
        </p>
        <div className="mr-actions-col">
          <button className="mr-btn mr-btn--danger" onClick={doBlock} disabled={busy === 'block'}>
            block {handle}
          </button>
          <button className="mr-btn mr-btn--ghost" onClick={() => setConfirmBlock(false)} disabled={busy === 'block'}>
            cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mr-card"
      ref={cardRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label={`message request from ${handle}`}
    >
      {/* A — the eyebrow; the handle below is the headline */}
      <div className="mr-head">
        <span className="mr-eyebrow">message request</span>
        <button className="mr-icon-btn" onClick={onClose} aria-label="close">
          <X size={13} />
        </button>
      </div>

      {/* B — identity, handle only */}
      <div className="mr-identity">
        <RequestAvatar handle={handle} src={requester.profileImageUrl} size={48} />
        <div className="mr-identity-text">
          <div className="mr-handle">{handle}</div>
          {unavailable ? (
            <div className="mr-sub">this account is no longer available</div>
          ) : (
            <div className="mr-meta">
              {requester.city && <span>{requester.city}</span>}
              {requester.city && activity && <span className="mr-dot">·</span>}
              {activity && <span className="mr-chip mr-chip--active">{activity}</span>}
            </div>
          )}
        </div>
      </div>

      {/* C — high-level details; collapses to whatever exists */}
      {!unavailable && hasDetails && (
        <div className="mr-details">
          {interests.length > 0 && (
            <div className="mr-interests">
              <span className="mr-label">{sharedCount > 0 ? 'you both like' : 'into'}</span>
              {interests.map((interest, i) => (
                <span key={interest} className={`mr-chip${i < sharedCount ? ' mr-chip--shared' : ''}`}>
                  {interest}
                </span>
              ))}
            </div>
          )}
          {requester.lookingFor && <div className="mr-line">looking for {requester.lookingFor}</div>}
          {requester.aboutMe && <div className="mr-line mr-line--dim mr-clamp-2">{requester.aboutMe}</div>}
        </div>
      )}

      {/* D — the message, in-app only */}
      {firstMessage?.text && (
        <div className="mr-message">
          <blockquote className="mr-quote">{firstMessage.text}</blockquote>
          <div className="mr-caption">
            sent as a request{sinceLabel(request.requestedAt) && ` · ${sinceLabel(request.requestedAt)}`}
          </div>
        </div>
      )}

      {/* E — exactly one primary */}
      <div className="mr-actions">
        <div className="mr-actions-row">
          {!unavailable && (
            <button
              className="mr-btn mr-btn--primary"
              ref={acceptRef}
              onClick={() => answer('accept')}
              disabled={!!busy}
            >
              accept
            </button>
          )}
          <button className="mr-btn mr-btn--secondary" onClick={() => answer('decline')} disabled={!!busy}>
            decline
          </button>
        </div>
        {error && <div className="mr-error">{error}</div>}
        <div className="mr-actions-foot">
          {unavailable ? (
            <span />
          ) : (
            <button className="mr-link" onClick={viewProfile} disabled={!!busy}>
              view profile
            </button>
          )}
          {!unavailable && (
            <div className="mr-overflow">
              <button
                className="mr-icon-btn mr-icon-btn--plain"
                aria-label="more"
                aria-expanded={overflowOpen}
                onClick={() => setOverflowOpen((v) => !v)}
                disabled={!!busy}
              >
                <MoreHorizontal size={16} />
              </button>
              {overflowOpen && (
                <div className="mr-menu" role="menu">
                  <button
                    className="mr-menu-item"
                    role="menuitem"
                    onClick={() => {
                      setOverflowOpen(false);
                      setConfirmBlock(true);
                    }}
                  >
                    block {handle}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* F — quiet, and never a warning colour */}
      <div className="mr-foot">
        <span className="mr-foot-note">declining is quiet — they won't be told</span>
        {more > 0 && (
          <button
            className="mr-link mr-link--small"
            onClick={() => {
              markPrompted(request.chatId);
              onClose();
              navigate('/messages');
            }}
          >
            {more} more in requests
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * The host. Mounted once, app-wide: it shows the oldest request this device
 * has not been prompted about, and never stacks or pages.
 */
export default function MessageRequestPrompt() {
  const navigate = useNavigate();
  const { requests } = useMessageRequests();
  const [dismissed, setDismissed] = useState<string[]>([]);
  const [toast, setToast] = useState('');

  const pending = requests.filter((r) => !hasBeenPrompted(r.chatId) && !dismissed.includes(r.chatId));
  // Oldest first: the person who has been waiting longest is asked about first.
  const current = pending.length > 0 ? pending[pending.length - 1] : null;

  const close = useCallback(() => {
    if (current) {
      markPrompted(current.chatId);
      setDismissed((d) => [...d, current.chatId]);
    }
  }, [current]);

  useEffect(() => {
    if (!current) return;
    const onKey = (e: KeyboardEvent) => {
      // Escape dismisses. It never declines — deciding takes a button.
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [current, close]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  function onAnswered(chatId: string, action: 'accept' | 'decline' | 'block') {
    markPrompted(chatId);
    setDismissed((d) => [...d, chatId]);
    if (action === 'accept') {
      const answered = requests.find((r) => r.chatId === chatId);
      const handle = answered?.requester.tetherHandle || '';
      setToast(`you're now chatting with ${handle.startsWith('@') ? handle : `@${handle}`}`);
      // The conversation page needs the recipient: without it the block check
      // never runs and the first reply is sent with an empty recipientId.
      navigate(`/messages/${chatId}`, {
        state: { recipientId: answered?.requester.id, recipientName: handle },
      });
    } else if (action === 'decline') {
      setToast('request hidden');
    } else {
      setToast('blocked');
    }
  }

  return (
    <>
      {current && (
        <div
          className="mr-scrim"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) close();
          }}
        >
          <MessageRequestCard
            request={current}
            more={pending.length - 1}
            source="prompt"
            onClose={close}
            onAnswered={onAnswered}
          />
        </div>
      )}
      {toast && <div className="mr-toast">{toast}</div>}
    </>
  );
}
